/** Ajout rapide d'une entreprise depuis le registre public.
 *
 *  Le cas d'usage est celui du téléphone : on connaît une enseigne, parfois une
 *  ville, jamais un SIRET. On cherche sur l'API publique recherche-entreprises
 *  (la source des campagnes), on reconnaît la bonne société à son adresse, et
 *  un clic crée la fiche complète — état civil, NAF, dirigeants, effectif,
 *  scoring, sectorisation.
 *
 *  Quatre critères, tous facultatifs (nom, adresse, activité, dirigeant), et
 *  l'utilisateur choisit comment les combiner : ET pour croiser, OU pour
 *  ratisser. Chercher par la seule adresse — « qui est installé rue des
 *  Halles ? » — est un usage à part entière, pas un repli.
 *
 *  La recherche est déclenchée explicitement (bouton ou Entrée) et non à la
 *  frappe : le serveur peut enchaîner plusieurs requêtes pour élargir, ce qui
 *  n'a pas à repartir à chaque lettre tapée.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";
import { toast } from "sonner";
import {
  Search, Zap, Plus, Loader2, Building2, MapPin, ExternalLink, Info, CheckCircle2,
  SlidersHorizontal, ChevronDown,
} from "lucide-react";
import { ageFromDate, formatAge, formatDateCreation } from "@/crm/anciennete";

const CHAMP = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-indigo-500";
const LABEL = "text-xs text-slate-500";

// Libellés des critères renvoyés par le serveur (badges des résultats en OU).
const CRITERE_LABEL = { nom: "nom", adresse: "adresse", naf: "activité", dirigeant: "dirigeant" };

/** Adresse affichable. Le registre livre déjà « 12 RUE DES HALLES 69001 LYON »
 *  dans `adresse` : y recoller le code postal et la ville les afficherait deux
 *  fois. On ne les reconstitue que si l'adresse manque (donnée non diffusible). */
function adresseCourte(c) {
  return c.adresse || [c.code_postal, c.ville].filter(Boolean).join(" ");
}

const euros = (n) => (typeof n === "number"
  ? n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
  : null);

function Ligne({ label, children }) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 py-1 text-xs border-b border-slate-100 last:border-0">
      <span className="text-slate-400">{label}</span>
      <div className="min-w-0 break-words text-slate-700">{children}</div>
    </div>
  );
}

/** Le bloc « Détail » d'un résultat : ce que le registre publie sur la société,
 *  déjà présent dans la réponse de recherche (aucun appel supplémentaire). De
 *  quoi la qualifier avant de l'ajouter : qui décide, quelle taille, quelle
 *  santé financière, où est le siège. */
function Detail({ c }) {
  const d = c.detail || {};
  const age = ageFromDate(d.date_creation);
  const finances = d.finances
    ? [d.finances.ca != null ? `CA ${euros(d.finances.ca)}` : null,
       d.finances.resultat_net != null ? `résultat ${euros(d.finances.resultat_net)}` : null]
      .filter(Boolean).join(" · ")
    : "";
  const lignes = [
    ["Création", d.date_creation
      ? `${formatDateCreation(d.date_creation)}${age !== null && age !== undefined ? ` · ${formatAge(age)}` : ""}` : null],
    ["Fermeture", d.date_fermeture ? formatDateCreation(d.date_fermeture) : null],
    ["Effectif", d.effectif_label ? `${d.effectif_label}${d.annee_effectif ? ` (${d.annee_effectif})` : ""}` : null],
    ["Forme", d.forme_juridique_label],
    ["Catégorie", d.categorie_entreprise],
    ["Sites", d.nombre_etablissements
      ? `${d.nombre_etablissements_ouverts ?? "?"} ouvert(s) sur ${d.nombre_etablissements}` : null],
    ["Siège", d.siege_adresse],
    ["Finances", finances ? `${finances} (${d.finances.annee})` : null],
    ["N° TVA", d.tva],
    ["Sigle", d.sigle],
    ["Convention", d.convention_collective ? `IDCC ${d.convention_collective}` : null],
    ["Labels", d.labels?.length ? d.labels.join(", ") : null],
  ].filter(([, v]) => v);
  const dirigeants = d.dirigeants || [];
  return (
    <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5"
      data-testid={`quickadd-detail-${c.siret || c.siren}`}>
      {dirigeants.length > 0 && (
        <Ligne label="Dirigeants">
          <ul className="space-y-0.5">
            {dirigeants.map((p, i) => (
              <li key={`${p.nom}-${i}`}>
                {p.nom}
                {p.qualite && <span className="text-slate-400"> — {p.qualite}</span>}
                {p.type === "morale" && <span className="text-slate-400"> (société)</span>}
              </li>
            ))}
          </ul>
        </Ligne>
      )}
      {lignes.map(([label, v]) => <Ligne key={label} label={label}>{v}</Ligne>)}
      {lignes.length === 0 && dirigeants.length === 0 && (
        <p className="py-1 text-xs text-slate-400">Le registre ne publie pas d'autre information sur cette société.</p>
      )}
      {c.siren && (
        <a href={`https://annuaire-entreprises.data.gouv.fr/entreprise/${c.siren}`} target="_blank" rel="noreferrer"
          className="my-1.5 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
          Fiche complète sur l'Annuaire des Entreprises <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function Resultat({ c, onAdd, adding, onOpen, montrerCriteres }) {
  const cessee = c.etat_administratif === "C";
  const [detail, setDetail] = useState(false);
  return (
    <div data-testid={`quickadd-result-${c.siret || c.siren}`}
      className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-slate-300">
      <div className="h-9 w-9 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
        <Building2 size={17} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-sm text-slate-900">{c.nom}</span>
          {c.nom_commercial && c.nom_commercial !== c.nom && (
            <span className="text-xs text-slate-500">({c.nom_commercial})</span>
          )}
          {cessee && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">CESSÉE</span>
          )}
          {c.est_siege && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">SIÈGE</span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate">
          <MapPin size={12} className="shrink-0" /> {adresseCourte(c) || "Adresse non publiée"}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">
          {[c.libelle_naf || c.naf, c.dirigeants?.join(", ")].filter(Boolean).join(" · ")}
        </div>
        <div className="text-[11px] font-mono text-slate-400 mt-0.5">{c.siret || c.siren}</div>
        {/* En OU, chaque ligne dit par quel(s) critère(s) elle est arrivée :
            sans ça, un résultat trouvé sur le seul dirigeant passerait pour
            une erreur de recherche. */}
        {montrerCriteres && c.criteres?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`quickadd-criteres-${c.siret || c.siren}`}>
            {c.criteres.map((k) => (
              <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                {CRITERE_LABEL[k] || k}
              </span>
            ))}
          </div>
        )}
        {/* Un autre établissement de la même société est déjà suivi : l'ajout
            reste légitime (une succursale se prospecte à part), on le signale
            seulement pour éviter la création involontaire d'un quasi-doublon. */}
        {c.meme_societe && (
          <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
            <Info size={11} /> Même société déjà au CRM{c.autre_etablissement ? ` (${c.autre_etablissement})` : ""}
          </div>
        )}
        {/* « Détail » : la ligne reste courte, le reste se déplie à la demande. */}
        <button type="button" data-testid={`quickadd-detail-toggle-${c.siret || c.siren}`}
          onClick={() => setDetail((v) => !v)} aria-expanded={detail}
          className="mt-1.5 flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700">
          <ChevronDown size={12} className={detail ? "rotate-180 transition-transform" : "transition-transform"} />
          {detail ? "Masquer le détail" : "Détail"}
        </button>
        {detail && <Detail c={c} />}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        {c.deja_en_base ? (
          <>
            <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Déjà au CRM</span>
            {c.company_id && (
              <button onClick={() => onOpen(c.company_id)}
                className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                Ouvrir <ExternalLink size={11} />
              </button>
            )}
          </>
        ) : (
          <button data-testid={`quickadd-add-${c.siret || c.siren}`} onClick={() => onAdd(c)} disabled={adding}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-50">
            {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Ajouter
          </button>
        )}
      </div>
    </div>
  );
}

const VIDE = { nom: "", adresse: "", naf: "", dirigeant: "" };

export default function QuickAddCompany({ onClose, onAdded }) {
  const navigate = useNavigate();
  const [f, setF] = useState(VIDE);
  const [operateur, setOperateur] = useState("et");
  const [inclureFermees, setInclureFermees] = useState(false);
  // Activité et dirigeant servent moins souvent que le nom et l'adresse : ils
  // restent repliés pour ne pas alourdir la saisie courante.
  const [avance, setAvance] = useState(false);
  const [nafs, setNafs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [res, setRes] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Catalogue NAF du CRM : les codes des secteurs travaillés, proposés en
  // suggestion. Un code hors catalogue reste saisissable à la main.
  useEffect(() => {
    api.get("/settings/naf-catalog")
      .then(({ data }) => setNafs((data || []).flatMap((d) => d.codes || [])))
      .catch(() => {});
  }, []);

  const actifs = Object.entries(f).filter(([, v]) => v.trim()).map(([k]) => k);

  const chercher = async () => {
    if (actifs.length === 0) return toast.error("Renseignez au moins un critère");
    if (f.nom.trim() && f.nom.trim().length < 2) return toast.error("Nom trop court");
    setLoading(true);
    try {
      const { data } = await api.get("/registre/recherche", {
        params: {
          nom: f.nom.trim() || undefined,
          adresse: f.adresse.trim() || undefined,
          naf: f.naf.trim() || undefined,
          dirigeant: f.dirigeant.trim() || undefined,
          operateur,
          inclure_fermees: inclureFermees,
        },
      });
      setRes(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Recherche impossible");
    }
    setLoading(false);
  };

  const ajouter = async (c) => {
    setAdding(c.siret || c.siren);
    try {
      const { data } = await api.post("/registre/ajout", { siren: c.siren, siret: c.siret || null });
      toast.success(data.created ? "Entreprise ajoutée depuis le registre public"
                                 : "Entreprise déjà au CRM — fiche mise à jour", {
        description: data.company?.nom,
        action: data.visible
          ? { label: "Ouvrir", onClick: () => navigate(`/entreprises/${data.company.id}`) }
          : undefined,
      });
      // La ligne ajoutée bascule sur place : on reste dans la liste pour en
      // ajouter d'autres sans relancer la recherche.
      setRes((r) => ({
        ...r,
        results: r.results.map((x) => (x.siret === c.siret && x.siren === c.siren
          ? { ...x, deja_en_base: true, company_id: data.visible ? data.company.id : null }
          : x)),
      }));
      onAdded?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Ajout impossible");
    }
    setAdding(null);
  };

  const onKey = (e) => { if (e.key === "Enter") chercher(); };

  return (
    <Modal onClose={onClose} size="2xl" testid="quickadd-modal" label="Ajout rapide d'une entreprise">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-9 w-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center"><Zap size={18} /></div>
        <h3 className="font-heading font-bold text-lg text-slate-900">Ajout rapide</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Cherchez l'entreprise sur le registre public (SIRENE / RNE). Tous les critères sont
        facultatifs : l'adresse seule répond à « qui est installé ici ? ».
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className={LABEL} htmlFor="quickadd-nom">Nom commercial ou enseigne</label>
          <input id="quickadd-nom" data-testid="quickadd-nom" autoFocus value={f.nom} onKeyDown={onKey}
            onChange={(e) => set("nom", e.target.value)} placeholder="Boulangerie des Halles, ou SIREN / SIRET"
            className={`mt-1 ${CHAMP}`} />
        </div>
        <div>
          <label className={LABEL} htmlFor="quickadd-adresse">Adresse, ville ou code postal</label>
          <input id="quickadd-adresse" data-testid="quickadd-adresse" value={f.adresse} onKeyDown={onKey}
            onChange={(e) => set("adresse", e.target.value)} placeholder="69001, Lyon, 12 rue des Halles…"
            className={`mt-1 ${CHAMP}`} />
        </div>
      </div>

      {avance && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="quickadd-avance">
          <div>
            <label className={LABEL} htmlFor="quickadd-naf">Activité (code NAF)</label>
            {/* Les codes du catalogue en suggestion ; la saisie reste libre
                (plusieurs codes séparés par une virgule, l'API en fait l'union). */}
            <input id="quickadd-naf" data-testid="quickadd-naf" list="quickadd-naf-codes" value={f.naf}
              onKeyDown={onKey} onChange={(e) => set("naf", e.target.value)} placeholder="56.10A"
              className={`mt-1 font-mono ${CHAMP}`} />
            <datalist id="quickadd-naf-codes">
              {nafs.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
            </datalist>
          </div>
          <div>
            <label className={LABEL} htmlFor="quickadd-dirigeant">Dirigeant</label>
            <input id="quickadd-dirigeant" data-testid="quickadd-dirigeant" value={f.dirigeant} onKeyDown={onKey}
              onChange={(e) => set("dirigeant", e.target.value)} placeholder="Jean Dupont"
              className={`mt-1 ${CHAMP}`} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-3">
        <div className="flex items-center gap-2">
          <label className={LABEL} htmlFor="quickadd-operateur">Combiner</label>
          <select id="quickadd-operateur" data-testid="quickadd-operateur" value={operateur}
            onChange={(e) => setOperateur(e.target.value)}
            className="px-2.5 py-2 rounded-lg border border-slate-300 text-sm bg-white">
            <option value="et">ET — tous les critères</option>
            <option value="ou">OU — au moins un critère</option>
          </select>
        </div>
        <button data-testid="quickadd-avance-toggle" onClick={() => setAvance((a) => !a)}
          aria-expanded={avance} aria-controls="quickadd-avance"
          className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
          <SlidersHorizontal size={14} /> {avance ? "Moins de critères" : "Plus de critères"}
        </button>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="quickadd-fermees" checked={inclureFermees}
            onChange={(e) => setInclureFermees(e.target.checked)} />
          Inclure les entreprises cessées
        </label>
        <button data-testid="quickadd-search" onClick={chercher} disabled={loading}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Rechercher
        </button>
      </div>

      {res && (
        <div className="mt-4" data-testid="quickadd-results">
          {/* La recherche a dû sortir des critères saisis : on le dit, sinon un
              résultat d'un autre département passerait pour une erreur. */}
          {res.elargi && res.results.length > 0 && (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Aucun résultat exact : recherche élargie ({res.strategie}).
            </div>
          )}
          {res.results.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              Aucune entreprise trouvée, même en élargissant. Vérifiez l'orthographe, retirez
              un critère, ou passez la combinaison en « OU ».
            </p>
          ) : (
            <>
              <div className="text-xs text-slate-400 mb-2">
                {res.results.length} résultat(s){res.total > res.results.length ? ` sur ${res.total}` : ""}
                {" · "}{res.operateur === "ou" ? `au moins un critère : ${res.strategie}` : res.strategie}
              </div>
              <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-1">
                {res.results.map((c) => (
                  <Resultat key={c.siret || c.siren} c={c} onAdd={ajouter}
                    adding={adding === (c.siret || c.siren)}
                    montrerCriteres={res.operateur === "ou" && res.criteres?.length > 1}
                    onOpen={(id) => { onClose(); navigate(`/entreprises/${id}`); }} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Fermer</button>
      </div>
    </Modal>
  );
}
